// App frame for logged-in screens: title bar + content + bottom tab bar.

import { h } from './dom';
import { api, type Counts } from '../api/client';
import { isLoggedIn } from '../auth/oidc';

export type CountKey = keyof Counts;
let counts: Counts = { bell: 0, friends: 0, rooms: 0, feed: 0 };
const badges = new Set<HTMLElement>();
const countListeners = new Set<(c: Counts) => void>();
export const onCounts = (fn: (c: Counts) => void) => { countListeners.add(fn); return () => countListeners.delete(fn); };
export const currentCounts = () => counts;

function paintBadges() {
  for (const b of badges) {
    if (!b.isConnected) { badges.delete(b); continue; }
    const key = b.dataset.count as CountKey;
    const n = counts[key] ?? 0;
    if (b.classList.contains('dot')) {
      b.hidden = n <= 0;
    } else {
      b.textContent = n > 99 ? '99+' : String(n);
      b.hidden = n <= 0;
    }
  }
}

/** Counts from a push message or an API response (no request). */
export function setCounts(c: Partial<Counts> | null | undefined) {
  if (!c) return;
  const num = (v: unknown, old: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, v | 0) : old);
  counts = { bell: num(c.bell, counts.bell), friends: num(c.friends, counts.friends), rooms: num(c.rooms, counts.rooms), feed: num(c.feed, counts.feed) };
  paintBadges();
  countListeners.forEach((fn) => fn(counts));
}

export function clearCounts() {
  setCounts({ bell: 0, friends: 0, rooms: 0, feed: 0 });
}

let inflight: Promise<void> | null = null;
let lastFetch = 0;
/**
 * One counts fetch (start-up, resume, tab change). Never on a timer: push messages
 * carry fresh counts. Calls within 2 s share one request.
 */
export function refreshCounts(force = false): Promise<void> {
  if (!isLoggedIn()) return Promise.resolve();
  if (inflight) return inflight;
  if (!force && Date.now() - lastFetch < 2000) return Promise.resolve();
  lastFetch = Date.now();
  inflight = api.counts().then(setCounts, () => { /* offline */ }).finally(() => (inflight = null));
  return inflight;
}

// Tab icons: small inline SVGs (crisper than emoji, follow currentColor).
const ICON: Record<string, string> = {
  feed: '<path d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4.5v-5.5h-5V20H5a1 1 0 0 1-1-1z"/>',
  friends: '<circle cx="9" cy="8.5" r="3.2"/><path d="M3.5 19c.6-3.3 2.8-5.2 5.5-5.2s4.9 1.9 5.5 5.2"/><circle cx="16.5" cy="9.5" r="2.5"/><path d="M15.2 14.2c2.6-.4 4.6 1.2 5.3 4.3"/>',
  bell: '<path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 1.8h-15z"/><path d="M10 20.5a2.2 2.2 0 0 0 4 0"/>',
  me: '<circle cx="12" cy="8.5" r="3.8"/><path d="M4.5 20c.9-4 3.8-6.2 7.5-6.2s6.6 2.2 7.5 6.2"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
};
const svg = (name: string) => {
  const s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  s.setAttribute('viewBox', '0 0 24 24');
  s.setAttribute('aria-hidden', 'true');
  s.setAttribute('class', 'tsvg');
  s.innerHTML = ICON[name];
  return s;
};

const TABS: { hash: string; icon: string; label: string; big?: boolean; count?: CountKey; dot?: boolean }[] = [
  { hash: '#/feed', icon: 'feed', label: 'Bảng tin', count: 'feed', dot: true },
  { hash: '#/friends', icon: 'friends', label: 'Bạn bè', count: 'friends' },
  { hash: '#/rooms', icon: 'plus', label: 'Chụp', big: true, count: 'rooms' },
  { hash: '#/notifications', icon: 'bell', label: 'Thông báo', count: 'bell' },
  { hash: '#/me', icon: 'me', label: 'Tôi' },
];

/** Renders the frame and returns the content element. */
export function shell(root: HTMLElement, opts: { title: string; tab?: string; back?: string; actions?: HTMLElement[]; brand?: boolean }) {
  const content = h('main.content', { id: 'main' });
  const tabs = h('nav.tabbar', { 'aria-label': 'Điều hướng chính' }, h('div.tabbar-in', {}, ...TABS.map((t) => {
    const badge = t.count ? h('span.tbadge', { hidden: true, class: t.dot ? 'dot' : '', 'data-count': t.count, 'aria-hidden': 'true' }) : null;
    if (badge) badges.add(badge);
    const on = opts.tab === t.hash;
    const inner = [h('span.ticon', {}, svg(t.icon), badge), h('span.tlabel', {}, t.label)];
    return h('a.tab-item', { href: t.hash, class: [on ? 'on' : '', t.big ? 'big' : ''].join(' '), 'aria-current': on ? 'page' : null,
      'aria-label': t.big ? 'Chụp booth cùng bạn' : null }, ...inner);
  })));
  root.replaceChildren(h('div.app', {},
    h('a.skip', { href: '#main', onclick: (e: Event) => { e.preventDefault(); content.focus(); } }, 'Tới nội dung'),
    h('header.topbar', {}, h('div.topbar-in', {},
      opts.back
        ? h('a.icon.back', { href: opts.back, title: 'Quay lại', 'aria-label': 'Quay lại' }, '←')
        : null,
      opts.brand
        ? h('h1.brand', {}, 'Booth', h('em', {}, 'Together'))
        : h('h1', {}, opts.title),
      h('div.tactions', {}, ...(opts.actions ?? [])))),
    content,
    tabs));
  content.tabIndex = -1;
  scrollTo(0, 0);
  paintBadges();
  // switching tabs is a natural moment to re-sync the badges (one request, no timer)
  if (opts.tab) void refreshCounts();
  return content;
}
